import { Link, Outlet } from 'react-router-dom'
import { useIsDesktop } from '../../hooks/useMediaQuery'
import { usePageTitleState, PageTitleProvider } from '../../hooks/usePageTitle'
import { TopBar } from '../navigation/TopBar'
import { BottomTabBar } from '../navigation/BottomTabBar'
import { Sidebar } from '../navigation/Sidebar'
import { ToastContainer } from '../ui/Toast'
import { TooltipProvider } from '../ui/Tooltip'
import { SupportReportWidget } from '../support/SupportReportWidget'
import { ImpersonationBanner } from '../auth/ImpersonationBanner'
import { useUIStore } from '../../stores/ui'
import { useAuthStore } from '../../stores/auth'
import { cn } from '../../lib/utils'

export function AppLayout() {
  const isDesktop = useIsDesktop()
  const pageTitleState = usePageTitleState()
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  // Only reserve space for the impersonation banner when it's actually shown —
  // otherwise its wrapper padding leaves a gap that pushes the page header down.
  const isImpersonating = useAuthStore((s) => Boolean(s.user?.impersonation?.active))

  return (
    <TooltipProvider delayDuration={300}>
      <PageTitleProvider value={pageTitleState}>
        <div className="relative min-h-screen overflow-hidden bg-surface-secondary">
          <div className="pointer-events-none fixed inset-0 shop-texture opacity-60" />
          <div className="pointer-events-none fixed inset-0 shop-grid-fade opacity-20" />
          <div className="relative z-[1]">
            {isDesktop ? (
              /* Desktop layout: Sidebar + content */
              <>
                <Sidebar />
                <div
                  className={cn(
                    'transition-[margin-left] duration-normal',
                    sidebarCollapsed ? 'ml-sidebar-sm' : 'ml-sidebar'
                  )}
                >
                  {isImpersonating && (
                    <div className="page-shell pt-4">
                      <ImpersonationBanner />
                    </div>
                  )}
                  <TopBar />
                  <main className="page-shell py-6">
                    <Outlet />
                  </main>
                  <footer className="page-shell pb-6">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-content-tertiary">
                      <span>Need help?</span>
                      <Link to="/help" className="text-content-link hover:underline">
                        Visit Help
                      </Link>
                      <span aria-hidden="true">&middot;</span>
                      <Link to="/terms" className="text-content-link hover:underline">
                        Terms
                      </Link>
                      <span aria-hidden="true">&middot;</span>
                      <Link to="/privacy" className="text-content-link hover:underline">
                        Privacy
                      </Link>
                    </div>
                  </footer>
                </div>
              </>
            ) : (
              /* Mobile layout: TopBar + content + BottomTabBar */
              <>
                {isImpersonating && (
                  <div className="px-4 pt-4">
                    <ImpersonationBanner />
                  </div>
                )}
                <TopBar />
                <main className="page-shell py-4 pb-[calc(var(--bottom-bar-height)+1rem+var(--safe-area-bottom))]">
                  <Outlet />
                  <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-content-tertiary">
                    <span>Need help?</span>
                    <Link to="/help" className="text-content-link hover:underline">
                      Visit Help
                    </Link>
                    <span aria-hidden="true">&middot;</span>
                    <Link to="/terms" className="text-content-link hover:underline">
                      Terms
                    </Link>
                    <span aria-hidden="true">&middot;</span>
                    <Link to="/privacy" className="text-content-link hover:underline">
                      Privacy
                    </Link>
                  </div>
                </main>
                <BottomTabBar />
              </>
            )}
          </div>
          <SupportReportWidget />
          <ToastContainer />
        </div>
      </PageTitleProvider>
    </TooltipProvider>
  )
}
